import * as React from "react";
import { Dictionary } from "../../../../../../react-core/Core/Dictionary";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Button } from "../../../../../../react-core/Html/Button";
import { Lightbox } from "../../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../../react-core/Html/FormComponent";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { ProductType } from "../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { DiscretePerformanceType } from "../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/DiscretePerformanceType";
import { FundPerformanceHistory } from "../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/FundPerformanceHistory";
import { Instrument } from "../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/Instrument";
import { ValuationPoint } from "../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/ValuationPoint";
import { IJsonDate } from "../../../../../../Wrap/System/IJsonDate";
import { ColumnChart } from "../../../../../Shared/Chart/ColumnChart";
import { ErrorMessagePopup } from "../../../../../Shared/Components/ErrorMessagePopup";
import { TableTempElement } from "../../../../../Shared/Components/TableTempElement";
import AdviserSiteTagManager from "../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { InvestmentsAction } from "../../PortfolioDashboard/Components/Investment/Action/InvestmentAction";
import { Contents, DiscretePerforamnceTypeDic } from "../../PortfolioDashboard/Components/Investment/Resource/Contents";
import { GraphHelper } from "./GraphHelper";
import { SDRLabelQuery } from "../../../../../../Wrap/Models/Domains/Investment/SDRLabelQuery";
import { SDRLabelClient } from "../../../../../../Wrap/Api/Instruments/SDRLabel";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";

interface IInvestmentDetailsPopupModalProps {
    showBox: boolean;
    callBack: (val: boolean) => void;
    instrumentCode: string;
    productId: number;
    instrumentName: string;
    asAt?: Date;
    unitCost: Money;
    unitPrice: Money;
    securityType: ProductType;
}

interface IInvestmentDetailsPopupModalState {
    frequency: DiscretePerformanceType;
    chartData: Dictionary<number, number>;
    chartId: string;
    chartType: string;
    isLoading: boolean;
    activeIndex?: number;
    isShowErrorMessage: boolean;
    instrument: Instrument;
    fundDiscretePerformance: Array<FundPerformanceHistory>;
    sdrUrl?: string;
    isUKSDROffshoreFund?: boolean;
    isUKDomicile?: boolean;
    scfdUrl?: string;
}

export class InvestmentDetailsPopupModal extends FormComponent<
    IInvestmentDetailsPopupModalProps,
    IInvestmentDetailsPopupModalState
> {
    constructor(props: IInvestmentDetailsPopupModalProps) {
        super(props);

        this.state = {
            frequency: DiscretePerformanceType.Y1, // default to one year
            chartId: "investment-popup-graph",
            chartType: "StockChart",
            chartData: null,
            isLoading: true,
            activeIndex: DiscretePerformanceType.Y1,
            isShowErrorMessage: false,
            instrument: null,
            fundDiscretePerformance: [],
        };
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    public componentDidMount() {
        this.getInstrumentDetails();
        this.getInstrumentValuationDistribution();
        this.getInstrumentDiscretePerformanceDetails();
        this.getConsumerFacingDisclosure();
        this.getSCFDUrl();
    }

    private openFactSheetModal() {
        const { instrument } = this.state;
        const url = instrument.FinexDocumentUrl;
        this.addEvent({
            event: "documentDownload",
            document: {
                title: `${instrument.DisplayName} - fund fact sheet`,
                url: url,
            },
        });
        const newWindow = window.open(url, "factSheet");
        newWindow.focus();
    }

    private openFinexDoc() {
        const { instrumentCode } = this.props;
        const { instrument } = this.state;
        const code: string = instrumentCode.split(".")[0];
        InvestmentsAction.getFinexDocument(code, (result) => {
            if (result != null && result != "") {
                this.addEvent({
                    event: "documentDownload",
                    document: {
                        title: `${instrument.DisplayName} - ${Contents.InvestmentKIDD}`,
                        url: result,
                    },
                });
                window.open(result);
            } else {
                this.setState({ isShowErrorMessage: true });
            }
        });
    }

    private openSCFDDoc() {
        const { scfdUrl } = this.state;
        const url = scfdUrl;
        this.addEvent({
            event: "documentDownload",
            document: {
                title: `${Contents.InvestmentSCFD}`,
                url: url,
            },
        });
        const newWindow = window.open(url, "factSheet");
        newWindow.focus();
    }

    private renderPriceGraph(): JSX.Element {
        const { activeIndex } = this.state;
        const frequencyList: JSX.Element[] = [];
        let elementRender: JSX.Element = this.renderLoading();

        DiscretePerforamnceTypeDic.forEach((x) => {
            let _btnClassName = "btn btn-period-select ";
            _btnClassName = _btnClassName + (activeIndex == x.key ? "active" : "");

            frequencyList.push(
                <div className="col-auto" key={"frequency" + x.key}>
                    <Button
                        type="button"
                        className={_btnClassName}
                        onClick={() => this.updatePriceHistory(x.key, x.key)}
                    >
                        {x.value}
                    </Button>
                </div>
            );
        });

        elementRender = (
            <div>
                <h3 className="mb-38" aria-hidden="true">
                    {Contents.InvestmentPriceHistory}
                </h3>
                <div aria-hidden="true" id={this.state.chartId} className="investment-popup-graph"></div>
                <div aria-hidden="true" className="period-select">
                    <div className="row narrow-gutter">{frequencyList}</div>
                </div>
            </div>
        );

        return elementRender;
    }

    private getInstrumentDetails() {
        const { instrumentCode } = this.props;

        InvestmentsAction.getInstrumentDetails(instrumentCode, (val) => {
            this.setState({
                instrument: val,
                isLoading: false,
            });
        });
    }

    private getConsumerFacingDisclosure() {
        const { productId } = this.props;

        InvestmentsAction.getConsumerFacingDisclosure(productId, (result) => {
            if (result && result.length > 0) {
                const sdrLabel = result[0];
                this.setState({
                    sdrUrl: sdrLabel.SDRUrl,
                    isUKSDROffshoreFund: sdrLabel.IsUKSDROffshoreFund,
                    isUKDomicile: sdrLabel.IsUKDomicile,
                });
            }
        });
    }

    private getSCFDUrl() {
        const { productId } = this.props;

        InvestmentsAction.getSCFDUrl(productId, (result) => {
            if (result && result.length > 0) {
                const scfdDoc = result[0];
                this.setState({
                    scfdUrl: scfdDoc.DocUrl,
                });
            }
        });
    }

    private getInstrumentDiscretePerformanceDetails() {
        const { productId } = this.props;

        InvestmentsAction.getInstrumentDiscretePerformanceDetails(productId, (val) => {
            this.setState({
                fundDiscretePerformance: val,
                isLoading: false,
            });
        });
    }

    private getInstrumentValuationDistribution() {
        const { instrumentCode } = this.props;
        const { frequency, chartId, chartType, isLoading } = this.state;

        const frequencyType = Frequency.Daily;
        const fromDate = this.getFromDate(frequency);
        const toDate = new Date();
        InvestmentsAction.getInstrumentValuationDistribution(
            instrumentCode,
            JsonDateParser.toJsonDateNoOffset(fromDate),
            JsonDateParser.toJsonDateNoOffset(toDate),
            frequencyType,
            1,
            2000,
            (val) => {
                this.setState(
                    {
                        chartData: this.getChartData(val, fromDate, toDate),
                        isLoading: false,
                    },
                    () => {
                        this.renderChart(chartId, chartType);
                    }
                );
            }
        );
    }

    private getChartData(chartdata: ValuationPoint[], fromDate: Date, toDate: Date): Dictionary<number, number> {
        const dic = {};
        const start = fromDate.setHours(0, 0, 0, 0);
        const end = toDate.setHours(0, 0, 0, 0);
        for (let date = new Date(start); date <= new Date(end); date.setDate(date.getDate() + 1)) {
            dic[date.getTime()] = null;
        }
        chartdata.forEach((x) => {
            dic[JsonDateParser.toDate(x.Date).getTime()] = x.Value.Amount;
        });
        return dic as Dictionary<number, number>;
    }

    public renderChart(chartId: string, chartType: string, isLoading?: boolean): any {
        const collectiveData = this.state.chartData;
        const chartDataSeries = [];

        if (collectiveData != null && !isLoading) {
            const navigatorDataSeries = [];

            var keys = Object.keys(collectiveData);
            keys.sort();

            for (var i = 0; i < keys.length; i++) {
                var key = keys[i];
                var value = collectiveData[key];
                if (value !== null) {
                    navigatorDataSeries.push({ x: Number(key), y: value, tooltip: "" });
                }
            }

            chartDataSeries.push({
                name: this.state.chartType,
                data: navigatorDataSeries,
                dataGrouping: {
                    enabled: false,
                },
                turboThreshold: 5000,
                lineWidth: 2,
                color: "rgba(0, 87, 183, 1)",
                id: 0,
            });
            GraphHelper.InvestmentsGraph(chartId, chartType, chartDataSeries);
        } else {
            GraphHelper.InvestmentsGraph(chartId, chartType, chartDataSeries);
        }
    }

    private renderLoading() {
        return <TableTempElement />;
    }

    private getMoneyFormat(val: Money, isShowCurrency?: boolean, decimalCount?: number): string {
        return MoneyDisplayHelper.FromatMoneyDisplay(val.Amount, val.Currency, isShowCurrency, decimalCount);
    }

    private updatePriceHistory(frequency: DiscretePerformanceType, index: number) {
        this.setState(
            {
                frequency: frequency,
                activeIndex: index,
                isLoading: true,
            },
            () => {
                this.getInstrumentValuationDistribution();
            }
        );
    }

    private getFromDate(frequency: DiscretePerformanceType) {
        let fmDate = null;
        const now = new Date();

        switch (frequency) {
            case DiscretePerformanceType.M1:
                fmDate = now.setMonth(now.getMonth() - 1);
                break;
            case DiscretePerformanceType.M3:
                fmDate = now.setMonth(now.getMonth() - 3);
                break;
            case DiscretePerformanceType.M6:
                fmDate = now.setMonth(now.getMonth() - 6);
                break;
            case DiscretePerformanceType.Y1:
                fmDate = now.setFullYear(now.getFullYear() - 1);
                break;
            case DiscretePerformanceType.Y2:
                fmDate = now.setFullYear(now.getFullYear() - 2);
                break;
            case DiscretePerformanceType.Y3:
                fmDate = now.setFullYear(now.getFullYear() - 3);
                break;
            case DiscretePerformanceType.Y4:
                fmDate = now.setFullYear(now.getFullYear() - 4);
                break;
            case DiscretePerformanceType.Y5:
                fmDate = now.setFullYear(now.getFullYear() - 5);
                break;
            default:
                fmDate = now;
        }

        return new Date(fmDate);
    }

    public render(): JSX.Element {
        return (
            <Lightbox
                cssWrapper="is-default-lightbox is-wide"
                show={this.props.showBox}
                onClose={() => this.props.callBack(false)}
                title={Contents.InvestmentInfoTitle}
            >
                {this.renderModalBody()}
                {this.renderErrorMessage()}
            </Lightbox>
        );
    }

    private renderErrorMessage() {
        return (
            <ErrorMessagePopup
                showPopup={this.state.isShowErrorMessage}
                errorMessage={Contents.ErrorMessage_FileFailedToOpen}
                onClose={() => {
                    this.setState({ isShowErrorMessage: false });
                }}
            />
        );
    }

    private renderModalBody() {
        const { securityType } = this.props;
        const isShareOrBond = [ProductType.Equity, ProductType.Bond].indexOf(securityType) > -1;
        return (
            <div>
                {this.renderHeader()}
                {this.renderPriceGraph()}
                {this.renderConsumerFacingDisclosure()}
                {this.renderAims()}
                {!isShareOrBond && this.renderCumulativePerformance()}
                {this.renderDiscretePerformance()}
                {this.renderNotice()}
            </div>
        );
    }

    private renderHeader() {
        const { instrumentName, instrumentCode, unitCost, unitPrice, asAt, securityType } = this.props;
        const displayDate = JsonDateParser.getFormatDateString(asAt, "dd MM yyyy", true, false);
        const isShareOrBond = [ProductType.Equity, ProductType.Bond].indexOf(securityType) > -1;
        const scfdUrl = this.state.scfdUrl;
        return (
            <>
                <div className="panel-outlined">
                    <div className="row">
                        <div className="col-fluid">
                            <h2 className="mb-2">{instrumentName}</h2>
                            {!isShareOrBond && (
                                <div className="col-fluid pl-0">
                                    <div className="pdf-panel">
                                        <button
                                            className="link-btn pdf-btn hyperlink-file pt-0"
                                            onClick={() => this.openFactSheetModal()}
                                        >
                                            <span className="icon" aria-hidden={true} />
                                            <span>{Contents.InvestmentFactSheet}</span>
                                        </button>
                                        <span> </span>
                                        <button
                                            className="link-btn pdf-btn hyperlink-file pt-0"
                                            onClick={() => this.openFinexDoc()}
                                        >
                                            <span className="icon" aria-hidden={true} />
                                            <span>{Contents.InvestmentKIDD}</span>
                                        </button>
                                        {scfdUrl && (
                                            <button
                                                className="link-btn pdf-btn hyperlink-file pt-0"
                                                onClick={() => this.openSCFDDoc()}
                                            >
                                                <span className="icon" aria-hidden={true} />
                                                <span>{Contents.InvestmentSCFD}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isShareOrBond && (
                                <div className="col-fluid pl-0">
                                    <div className="pdf-panel">
                                        {scfdUrl && (
                                            <button
                                                className="link-btn pdf-btn hyperlink-file pt-0"
                                                onClick={() => this.openSCFDDoc()}
                                            >
                                                <span className="icon" aria-hidden={true} />
                                                <span>{Contents.InvestmentSCFD}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-auto col-middle">
                            <div className="text-size-14">
                                {Contents.InvestmentUnitPriceAt} {Contents.InvestmentUnitAsAt} {displayDate}
                            </div>
                            <div className="text-size-18 text-highlight">{this.getMoneyFormat(unitPrice, true, 4)}</div>
                        </div>
                        <div className="col-auto col-middle">
                            <div>
                                <span className="text-size-14 mr-4">{Contents.InvestmentUnitCost}</span>
                            </div>
                            <div className="text-size-18 text-highlight">{this.getMoneyFormat(unitCost, true, 4)}</div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    private renderConsumerFacingDisclosure() {
        const { sdrUrl } = this.state;

        if (!sdrUrl || sdrUrl.trim() === "") {
            return null;
        }

        return (
            <div>
                <img
                    src={sdrUrl}
                    alt="Consumer Facing Disclosure"
                    style={{ maxWidth: "350px", width: "100%", height: "auto" }}
                />
                <hr className="mt-50 mb-50" />
            </div>
        );
    }

    private renderAims() {
        return (
            <>
                <h2>Aims</h2>
                <div className="text-size-14 text-justify">{Contents.Aims}</div>
                <hr className="mt-50 mb-50" />
            </>
        );
    }

    private renderCumulativePerformance() {
        const { instrument, isLoading } = this.state;
        if (isLoading) {
            return this.renderLoading();
        } else if (instrument && instrument.ManagedFund) {
            const cumulativePerformance = [];
            instrument.ManagedFund.DiscretePerformances.forEach((value, index) => {
                const item = DiscretePerforamnceTypeDic.filter((x) => x.key == value.Type)[0];
                if (item) {
                    cumulativePerformance.push({ SectorName: item.value, Value: value.Rate });
                }
            });
            return (
                <>
                    <h2 className="mb-30">{Contents.CumulativePerformance}</h2>
                    <div className="mw-530">
                        <ColumnChart
                            cssWrapper="is-cumulative-performance mb-50"
                            data={cumulativePerformance}
                            height={200}
                        />
                    </div>
                </>
            );
        }
    }

    private renderDiscretePerformance() {
        const { instrument, isLoading, fundDiscretePerformance } = this.state;

        if (isLoading) {
            return this.renderLoading();
        } else if (fundDiscretePerformance.length > 0) {
            const date = JsonDateParser.getFormatDate(
                fundDiscretePerformance[fundDiscretePerformance.length - 1].EndDate,
                "dd/MM/yyyy"
            );
            const discretePerformances = [];
            fundDiscretePerformance.forEach((value, index) => {
                discretePerformances.push({
                    SectorName: JsonDateParser.getFormatDate(value.EndDate, "yyyy"),
                    Value: value.Performance,
                });
            });
            return (
                <>
                    <h2 className="mb-8">{Contents.DiscretePerformance}</h2>
                    <div className="text-bold font-size-18 mb-30">12 months to {date}</div>
                    <div className="mw-530">
                        <ColumnChart
                            cssWrapper="is-discrete-performance mb-50"
                            data={discretePerformances}
                            height={184}
                        />
                    </div>
                </>
            );
        }
    }

    private renderNotice(): JSX.Element {
        const { isUKDomicile, isUKSDROffshoreFund } = this.state;

        const noticeTop: JSX.Element = (
            <div className="text-justify">
                <p className="text-size-14 text-bold mb-8">{Contents.NoticeTopContent}</p>
                <p className="text-size-14  mb-8">{Contents.NoticeContent}</p>
                <p className="text-size-14 text-bold">{Contents.NoticeBottomContent}</p>

                {!isUKDomicile && <Alert alertType={AlertType.Warning} message={Contents.NonUKDomicileWarning} />}

                {/* {isUKSDROffshoreFund && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            "This product is based overseas and is not subject to UK sustainable investment labelling and disclosure requirements"
                        }
                    />
                )} */}
            </div>
        );
        return noticeTop;
    }
}
